import {
  Banner,
  Box,
  Divider,
  DropdownButtonContext,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback, useContext, useMemo, useState } from 'react';

import { aiApi } from 'apis/ai';
import { AiBanner } from 'components/aiBanner';
import { AiLoadingState } from 'components/aiLoadingState';
import { CopilotDropdownActionContext } from 'components/copilotDropdown';
import { HtmlRenderer } from 'components/htmlRenderer';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { sanitizationProfile, useEditorContext } from 'utils/useEditorContext';
import { LanguageSelectionListValue } from 'utils/useLanguageSelectionList';

import {
  CopilotDropdownFooter,
  CopilotDropdownHeader,
} from '../../../copilotDropdown/components';
import { EditorCopilotDropdownContentBox } from '../contentBox';

import {
  CopilotLanguageInputSelectionList,
  CopilotTranslateOnLanguageChange,
} from './components';

const additionalFeedback = {
  feature: 'translate',
};

type CopilotDropdownTranslateProps = {
  readonly initialText?: string;
};

/**
 * Represents the component that translates selected text or entire content of
 * the editor.
 */
export const EditorCopilotDropdownTranslate = memo(
  function EditorCopilotDropdownTranslate({
    initialText,
  }: CopilotDropdownTranslateProps) {
    const { onBack, actionsComputed } = useContext(
      CopilotDropdownActionContext,
    );
    const csid = useAppSelector(
      (state) => state.conversation.metaConversation.conversation.id,
    );
    const actionComputed = actionsComputed.translate;
    const { insertBelow, isEmpty, replace, getSelectedText } =
      useEditorContext();
    const dropdownContext = useContext(DropdownButtonContext);
    const [translate, { data }] =
      aiApi.useLazyReadStreamAiTextTranslationQuery();
    const [selectedLanguage, setSelectedLanguage] =
      useState<LanguageSelectionListValue>();

    const translatedText = useMemo(() => {
      return data?.text ?? undefined;
    }, [data?.text]);

    const content = useMemo(() => {
      if (!isEmpty && !initialText && !selectedLanguage) {
        return (
          <AiBanner>
            Choose a language to view the translation preview displayed here.
          </AiBanner>
        );
      }

      if (!data && initialText) {
        return <AiBanner>{initialText}</AiBanner>;
      }

      if (data?.meta?.isLoading) {
        return <AiLoadingState label="" />;
      }

      if (data?.meta?.isError) {
        return (
          <Banner
            variant="fail"
            description={
              <span>
                An error occurred while translating into the selected language.
              </span>
            }
          />
        );
      }

      if (isEmpty && !data?.meta?.isStreaming) {
        return (
          <Banner
            variant="warning"
            description={<span>There is no text to translate.</span>}
          />
        );
      }

      if (translatedText) {
        return (
          <HtmlRenderer
            sanitizationProfileType={sanitizationProfile}
            html={translatedText}
          />
        );
      }
    }, [data, initialText, isEmpty, selectedLanguage, translatedText]);

    /** Handles the change event of languages selection list. */
    const handleLanguageChange = useCallback<CopilotTranslateOnLanguageChange>(
      async (event) => {
        setSelectedLanguage(event.selectedLanguage);

        if (!event.selectedLanguage?.name) {
          return;
        }

        const text = getSelectedText();

        try {
          await translate({
            language: event.selectedLanguage.name,
            text,
            ...(csid ? { csid: csid.toString() } : {}),
          });
        } catch {
          // Already handled via asyncStatus
        }
      },
      [csid, getSelectedText, translate],
    );

    const handleInsertBelow = useCallback(() => {
      if (!translatedText) {
        return;
      }

      insertBelow(translatedText);

      dropdownContext.closeDropdown();

      ampli.agentCopilotTextInsertBelowClicked({
        key: 'translate',
      });
    }, [translatedText, dropdownContext, insertBelow]);

    const handleReplace = useCallback(() => {
      if (!translatedText) {
        return;
      }

      replace(translatedText);

      dropdownContext.closeDropdown();

      ampli.agentCopilotTextReplaceClicked({
        key: 'translate',
      });
    }, [translatedText, dropdownContext, replace]);

    return (
      <Box style={{ maxWidth: 768, minWidth: 540 }}>
        <CopilotDropdownHeader
          title={actionComputed.title}
          onBack={onBack}
          feedbackId={data?.feedback_id ?? ''}
          additionalFeedback={additionalFeedback}
        >
          <CopilotLanguageInputSelectionList
            onLanguageChange={handleLanguageChange}
            selectedLanguageCode={selectedLanguage?.code}
            disabled={isEmpty}
          />
        </CopilotDropdownHeader>

        <EditorCopilotDropdownContentBox isEmphasized={!!translatedText}>
          {content}
        </EditorCopilotDropdownContentBox>

        <Divider />

        <CopilotDropdownFooter
          handleInsertBelow={handleInsertBelow}
          handleReplace={handleReplace}
          disableInsertBelow={!translatedText}
          disableReplace={!translatedText}
          draftContent={translatedText}
          disableImproveReply={!translatedText}
        />
      </Box>
    );
  },
);
